import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Card, Col, Row, Statistic, Table, Tag, Tooltip, Typography } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Line } from '@ant-design/charts';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { adminApi } from '@services/api';
import { reportLoadErrorMessage } from '@/utils/apiError';
import type { GroupAnalyticsParams, MatchesPerUserRow } from '@/types';

const { Text } = Typography;

const PAYERS = 'Payers';
const NON_PAYERS = 'Non-payers';

// Fixed per segment so a range with only one segment never repaints the other.
// Checked for colour-blind separation and contrast against a white card.
const SEGMENT_COLORS: Record<string, string> = {
  [PAYERS]: '#d46b08',
  [NON_PAYERS]: '#1677ff',
};

const segmentLabel = (isPayer: boolean) => (isPayer ? PAYERS : NON_PAYERS);

const formatDay = (day: string) => dayjs(day).format('D MMM YYYY');

const formatRatio = (v: number | null | undefined) =>
  v === null || v === undefined
    ? '—'
    : v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

interface ChartPoint {
  day: string;
  segment: string;
  /** null means that segment had no active users that day — drawn as a gap, never as 0. */
  matchesPerUser: number | null;
}

interface SegmentTotal {
  label: string;
  activeUserDays: number;
  matches: number;
  /** Total matches over total active user-days, not an average of daily ratios. */
  matchesPerUser: number | null;
}

// Chart config lives at module scope so re-renders do not rebuild the chart.
const CHART_POINT = { sizeField: 3 };
const CHART_LEGEND = { color: { position: 'top' as const } };
const CHART_SCALE = {
  color: {
    domain: [PAYERS, NON_PAYERS],
    range: [SEGMENT_COLORS[PAYERS], SEGMENT_COLORS[NON_PAYERS]],
  },
};
const CHART_AXIS = {
  x: {
    labelAutoHide: true,
    labelFormatter: (d: string) => dayjs(d).format('D MMM'),
  },
};
const CHART_TOOLTIP = {
  title: (d: ChartPoint) => formatDay(d.day),
  items: [
    {
      channel: 'y' as const,
      valueFormatter: (v: number | null) => (v === null ? 'No active users' : formatRatio(v)),
    },
  ],
};

const columns: ColumnsType<MatchesPerUserRow> = [
  {
    title: 'Day',
    dataIndex: 'day',
    key: 'day',
    render: (d: string) => formatDay(d),
    sorter: (a, b) => a.day.localeCompare(b.day),
  },
  {
    title: 'Segment',
    dataIndex: 'isPayer',
    key: 'segment',
    render: (isPayer: boolean) => (
      <Tag color={isPayer ? 'orange' : 'blue'}>{segmentLabel(isPayer)}</Tag>
    ),
    filters: [
      { text: PAYERS, value: true },
      { text: NON_PAYERS, value: false },
    ],
    onFilter: (value, r) => r.isPayer === value,
  },
  {
    title: 'Active users',
    dataIndex: 'activeUsers',
    key: 'activeUsers',
    align: 'right',
    render: (n: number) => n.toLocaleString('en-IN'),
    sorter: (a, b) => a.activeUsers - b.activeUsers,
  },
  {
    title: 'Matches',
    dataIndex: 'matches',
    key: 'matches',
    align: 'right',
    render: (n: number) => n.toLocaleString('en-IN'),
    sorter: (a, b) => a.matches - b.matches,
  },
  {
    title: 'Matches/user',
    dataIndex: 'matchesPerUser',
    key: 'matchesPerUser',
    align: 'right',
    render: (v: number | undefined) => formatRatio(v),
    sorter: (a, b) => (a.matchesPerUser ?? -1) - (b.matchesPerUser ?? -1),
  },
];

/**
 * Matches played per active user per IST day, split by payers and non-payers.
 *
 * The headline figures pool the whole range (sum of matches over sum of
 * active user-days) so a quiet day with a handful of payers does not weigh the
 * same as a busy one. A segment missing on a day is a gap in the line, not a
 * zero — nobody in that segment was active, which is not the same as nobody
 * playing.
 */
export const MatchesPerUserCard: React.FC<{ params: GroupAnalyticsParams }> = ({ params }) => {
  const [rows, setRows] = useState<MatchesPerUserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.getMatchesPerUser(params);
      setRows(res.data ?? []);
      setError(null);
    } catch (e) {
      setError(reportLoadErrorMessage(e, 'Could not load matches per active user'));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    void load();
  }, [load]);

  const totals = useMemo<SegmentTotal[]>(
    () =>
      [true, false].map((isPayer) => {
        const segmentRows = rows.filter((r) => r.isPayer === isPayer);
        const activeUserDays = segmentRows.reduce((s, r) => s + r.activeUsers, 0);
        const matches = segmentRows.reduce((s, r) => s + r.matches, 0);
        return {
          label: segmentLabel(isPayer),
          activeUserDays,
          matches,
          matchesPerUser: activeUserDays > 0 ? matches / activeUserDays : null,
        };
      }),
    [rows]
  );

  // One point per day per segment, so a segment with no active users that day
  // breaks the line instead of being joined straight across.
  const chartData = useMemo<ChartPoint[]>(() => {
    const byKey = new Map(rows.map((r) => [`${r.day}|${r.isPayer}`, r]));
    const days = Array.from(new Set(rows.map((r) => r.day))).sort();
    return days.flatMap((day) =>
      [true, false].map((isPayer) => ({
        day,
        segment: segmentLabel(isPayer),
        matchesPerUser: byKey.get(`${day}|${isPayer}`)?.matchesPerUser ?? null,
      }))
    );
  }, [rows]);

  return (
    <Card
      loading={loading}
      title={
        <span>
          Matches per active user{' '}
          <Tooltip title="Active users come from the daily activity log (one row per user per IST day), so days before activity tracking started under-report. A payer has made at least one real purchase ever; sandbox and tester purchases do not count. Headline figures divide total matches by total active user-days, not an average of the daily ratios.">
            <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
          </Tooltip>
        </span>
      }
    >
      {error && <Alert type="warning" showIcon message={error} style={{ marginBottom: 12 }} />}

      {rows.length === 0 ? (
        !error && (
          <Text type="secondary">
            No active users in this range. This report reads the daily activity log, which only has
            rows from when activity tracking started, so pick days after that.
          </Text>
        )
      ) : (
        <>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            {totals.map((t) => (
              <Col span={12} key={t.label}>
                <Statistic
                  title={t.label}
                  value={formatRatio(t.matchesPerUser)}
                  suffix={
                    <Text type="secondary" style={{ fontSize: 14 }}>
                      matches per user
                    </Text>
                  }
                />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {t.activeUserDays > 0
                    ? `${t.matches.toLocaleString('en-IN')} matches over ${t.activeUserDays.toLocaleString('en-IN')} active user-days`
                    : `No active ${t.label.toLowerCase()} in this range`}
                </Text>
              </Col>
            ))}
          </Row>

          <Line
            data={chartData}
            xField="day"
            yField="matchesPerUser"
            colorField="segment"
            height={220}
            point={CHART_POINT}
            legend={CHART_LEGEND}
            scale={CHART_SCALE}
            axis={CHART_AXIS}
            tooltip={CHART_TOOLTIP}
          />

          <Table
            style={{ marginTop: 16 }}
            size="small"
            rowKey={(r) => `${r.day}-${r.isPayer ? 'payer' : 'non-payer'}`}
            dataSource={rows}
            columns={columns}
            pagination={rows.length > 15 ? { pageSize: 10, showSizeChanger: false } : false}
          />
        </>
      )}
    </Card>
  );
};
